import React, { FunctionComponent, useMemo } from 'react';

import { Metadata } from '@gojob/cms';
import { Location } from '@reach/router';
import { Helmet } from 'react-helmet';

import getLangFromLocale from './get-lang-from-locale';
import useSiteMetadata from './useSiteMetadata';

export interface SeoProps extends Metadata {
  i18n_lang?: string;
  canonicalUrl?: string;
}

/** The SEO oriented component which sets page title and various meta including description, og and twitter */
const Seo: FunctionComponent<SeoProps> = (props) => {
  const { title, description, noindex, ogImage, ogImageAlt, i18n_lang, canonicalUrl } = props;
  const imageSrc = ogImage?.asset?.localFile?.childImageSharp?.original?.src;

  const { siteName, siteUrl } = useSiteMetadata();

  const cleanSiteUrl = useMemo(() => siteUrl.replace(/\/+$/, ''), [siteUrl]);
  const lang = useMemo(() => getLangFromLocale(i18n_lang), [i18n_lang]);

  const imageUrl = useMemo(() => {
    if (imageSrc) {
      return `${cleanSiteUrl}${imageSrc}`;
    }
    return '';
  }, [imageSrc, cleanSiteUrl]);

  return (
    <Location>
      {({ location }) => {
        return (
          <Helmet htmlAttributes={{ lang }}>
            {/** Default html meta */}
            {title && <title data-testid="Title">{title}</title>}
            {description && <meta data-testid="Description" name="description" content={description} />}

            {/** NoIndex meta https://support.google.com/webmasters/answer/93710?hl=en */}
            {noindex && <meta data-testid="NoIndex" name="robots" content="noindex" />}

            {/** Open graph meta */}
            {title && <meta data-testid="OG-Title" name="og:title" content={title} />}
            {description && <meta data-testid="OG-Description" name="og:description" content={description} />}
            {cleanSiteUrl && (
              <meta data-testid="OG-Url" name="og:url" content={`${cleanSiteUrl}${location.pathname}`} />
            )}
            {siteName && <meta data-testid="OG-SiteName" name="og:site_name" content={siteName} />}
            {imageUrl && <meta data-testid="OG-Image" name="og:image" content={imageUrl} />}
            {ogImageAlt && <meta data-testid="OG-ImageAlt" name="og:image:alt" content={ogImageAlt} />}

            {/** Twitter meta */}
            <meta name="twitter:card" content="summary_large_image" />
            {title && <meta data-testid="Twitter-Title" name="twitter:title" content={title} />}
            {description && (
              <meta data-testid="Twitter-Description" name="twitter:description" content={description} />
            )}
            {imageUrl && <meta data-testid="Twitter-Image" name="twitter:image" content={imageUrl} />}
            {canonicalUrl && <link data-testid="Canonical" rel="canonical" href={canonicalUrl} />}

            {/** Facebook meta (or Meta meta 😆😆😆😆😆😆😆😆😆) */}
            <meta name="facebook-domain-verification" content="b9aotgt4jde9mwb8mkm4muq58ke8fd" />
          </Helmet>
        );
      }}
    </Location>
  );
};

export default Seo;
